import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import RsTable from '@Pages/RetroSynthesis/components/RsTable';
import RsBuildingBlocksAlignment, {
  ALIGN_CENTER_STARTING_MATERIALS,
} from '@Pages/RetroSynthesis/components/RsBuildingBlocks/RsBuildingBlocksAlignment';
import RsProjectRoutesToolbar from '@Pages/RetroSynthesis/ProjectRoutes/components/RsProjectRoutesToolbar';
import RsProjectRouteItem from '@Pages/RetroSynthesis/ProjectRoutes/components/RsProjectRouteItem';
import ProjectRoutesTableHeader from '@Pages/RetroSynthesis/ProjectRoutes/components/ProjectRoutesTableHeader';
import PropTypes from 'prop-types';
import RsPaging from '@Pages/RetroSynthesis/components/RsPaging';
import { extractCurrentSortOption } from '@Pages/RetroSynthesis/RetroSynthesisProjects/RetroSynthesisProjects';
import includes from 'lodash/includes';
import noop from 'lodash/noop';

import RouteOverviewTreeTooltipContainer from '@Pages/RetroSynthesis/RouteTree/RouteOverviewTreeTooltipContainer';
import { onRoutesDelete, onRouteViewClick } from '@Pages/RetroSynthesis/ProjectRoutes/analytics';
import { RouteType, UrlParams } from '@Pages/RetroSynthesis/constants';
import RsModal from '@Pages/RetroSynthesis/components/RsModal';
import DialogWithConfirmationLayout from '@Containers/Dialogs/DialogsWithIcon/DialogWithConfirmationLayout';
import { focusOnElement } from '../utils';
/* eslint-disable react-hooks/exhaustive-deps */
function ProjectRoutes({
  projectId,
  response,
  isLoading,
  selectedIds,
  setSelectedIds,
  projectStructure,
  doChanges,
  doZoomStructure,
  synthesisMenuOptions,
  isConfidenceColumnShown,
  isFirstDisconnectionColumnShown,
  doDeleteRoutes,
  routesCount,
  projectInfo,
  lastViewedRoutesPosition,
  setViewRoutesPosition,
}) {
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  const tableRef = useRef();
  const { content = [], totalPages, size, number, totalElements, sort } = response || {};

  const handleChanges = (newParams) => {
    if (!newParams) {
      return;
    }
    // In case 'page' or changed, we should keep defined sorting option
    // In case or 'sort' or 'size' changed we should set page to 0
    const params = newParams.page ? { size, sort: extractCurrentSortOption(sort), ...newParams } : { size, page: 0, ...newParams };
    doChanges(projectId, { ...params });
  };

  useEffect(() => {
    if (response && totalElements > 0 && lastViewedRoutesPosition && !isLoading) {
      focusOnElement(`project-route-right-hand-link-${lastViewedRoutesPosition.id}`);
      setViewRoutesPosition(null);
    }
  }, [response]);

  const handleDeleteClick = () => {
    setShowDeletionDialog(true);
  };

  const handleDeleteRoutes = () => {
    const routeIdsToDelete = [...selectedIds];
    setShowDeletionDialog(false);
    doDeleteRoutes(projectId, routeIdsToDelete);
    // AA
    onRoutesDelete(
      projectId,
      response.content.filter((r) => routeIdsToDelete.includes(r.id)),
      projectInfo
    );
  };

  const selectionChangeHandler = (e) => {
    const { targetId, value } = e || {};
    if (!targetId || !selectedIds) {
      return;
    }
    if (value && selectedIds.indexOf(targetId) === -1) {
      setSelectedIds([...selectedIds, targetId]);
    } else if (!value) {
      setSelectedIds(selectedIds.filter((id) => id !== targetId));
    }
  };
  const isAllOnPageSelected = content.length === selectedIds.length;
  let deleteMessage = '';
  if (showDeletionDialog) {
    const isAllInProjectSelected = totalElements === selectedIds.length;
    const allOfTypesSelected = RouteType.allTypes.reduce((result, type) => {
      if (
        content?.filter((route) => route.routeType === type && includes(selectedIds, route.id)).length === routesCount[type] &&
        routesCount[type] > 0
      ) {
        result.push(type.toLowerCase());
      }
      return result;
    }, []);

    let routesDeleteMessage = 'Are you sure?';
    if (allOfTypesSelected.length) {
      routesDeleteMessage = `All ${allOfTypesSelected.join(' and ')} routes will be deleted. Do you want to continue?`;
    }

    deleteMessage = isAllInProjectSelected ? 'Do you want to delete the whole project?' : routesDeleteMessage;
  }

  const handleRouteViewClick = (routeId) => {
    onRouteViewClick(
      projectId,
      content.filter((r) => routeId === r.id),
      projectInfo
    );
    setViewRoutesPosition({ scrollTop: tableRef?.current.scrollTop, id: routeId });
  };

  return (
    <div className="retro-synthesis-projects">
      {}
      <div className="retro-synthesis-projects__body" id="main-content">
        <RouteOverviewTreeTooltipContainer />
        {showDeletionDialog && (
          <RsModal showBackdrop>
            <DialogWithConfirmationLayout
              size="md"
              character="!"
              layoutType="info"
              header="Delete selected route/s"
              selfClose={() => setShowDeletionDialog(false)}
              onYesClick={handleDeleteRoutes}>
              {deleteMessage}
            </DialogWithConfirmationLayout>
          </RsModal>
        )}
        <RsProjectRoutesToolbar
          onExportClick={() => null}
          selectedCount={(selectedIds && selectedIds.length) || 0}
          onSelectAllClick={() => setSelectedIds(!isAllOnPageSelected ? content.map((projectVO) => projectVO.id) : [])}
          isAllSelected={isAllOnPageSelected}
          displayedItems={(content && content.length) || 0}
          onLimitToClick={() => handleChanges({ [UrlParams.LIMIT_TO]: selectedIds })}
          onDeleteClick={handleDeleteClick}
        />
        <RsBuildingBlocksAlignment
          alignMode={ALIGN_CENTER_STARTING_MATERIALS}
          effectList={[content]}
          containerSelector=".table-row__blocks"
        />
        <RsTable
          className={cn('rs-project-routes-table')}
          isLoading={isLoading}
          showFooter
          dataList={content}
          renderItem={(projectRouteVO) => (
            <RsProjectRouteItem
              {...projectRouteVO}
              onSelectionChange={selectionChangeHandler}
              selected={includes(selectedIds, projectRouteVO.id)}
              projectId={projectId}
              synthesisMenuOptions={synthesisMenuOptions}
              onRouteViewClick={handleRouteViewClick}
              isConfidenceColumnShown={isConfidenceColumnShown}
              projectInfo={projectInfo}
              isFirstDisconnectionColumnShown={isFirstDisconnectionColumnShown}
            />
          )}
          header={
            <ProjectRoutesTableHeader
              sort={sort}
              onSortChange={handleChanges}
              projectStructure={projectStructure}
              onStructureClick={doZoomStructure}
              isConfidenceColumnShown={isConfidenceColumnShown}
              isFirstDisconnectionColumnShown={isFirstDisconnectionColumnShown}
            />
          }
          ref={tableRef}>
          {totalElements && (
            <RsPaging
              pageSize={size}
              currentPage={number + 1}
              totalPages={totalPages}
              totalItems={totalElements}
              onChange={handleChanges}
            />
          )}
        </RsTable>
      </div>
    </div>
  );
}

ProjectRoutes.defaultProps = {
  selectedIds: [],
  doDeleteRoutes: noop,
};

ProjectRoutes.propTypes = {
  projectId: PropTypes.string.isRequired,
  response: PropTypes.object,
  isLoading: PropTypes.bool,
  doFetchProjectRoutes: PropTypes.func,
  selectedIds: PropTypes.array,
  setSelectedIds: PropTypes.func,
  projectStructure: PropTypes.string,
  doZoomStructure: PropTypes.func,
  synthesisMenuOptions: PropTypes.array,
  isConfidenceColumnShown: PropTypes.bool,
  isFirstDisconnectionColumnShown: PropTypes.bool,
  doDeleteRoutes: PropTypes.func,
  routesCount: PropTypes.object,
  projectInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  doChanges: PropTypes.func,
  lastViewedRoutesPosition: PropTypes.object,
  setViewRoutesPosition: PropTypes.func,
};

export default ProjectRoutes;
